import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { TokenService } from '../services/tokenService';
import { firestoreService } from '../services/firestoreService';

export interface TokenUser {
  sessionId: string;
  token: string;
  expiresAt: number;
}

interface AuthContextType {
  currentUser: User | null;
  tokenUser: TokenUser | null;
  isTokenUser: boolean;
  signup: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  getRemainingTime: () => { hours: number; minutes: number; isExpired: boolean } | null;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  tokenUser: null,
  isTokenUser: false,
  signup: async () => {},
  login: async () => {},
  loginWithToken: async () => {},
  logout: async () => {},
  getRemainingTime: () => null,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [tokenUser, setTokenUser] = useState<TokenUser | null>(null);
  const [loading, setLoading] = useState(true);

  const signup = (email: string, password: string) => {
    return createUserWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        setCurrentUser(userCredential.user);
      });
  };

  const login = (email: string, password: string) => {
    return signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        setCurrentUser(userCredential.user);
      });
  };

  const loginWithToken = async (token: string) => {
    if (!token || token.trim().length === 0) {
      throw new Error('Token is required');
    }

    const trimmedToken = token.trim();

    // Validate token exists in Firestore and hasn't expired
    const tokenHash = TokenService.hashToken(trimmedToken);
    const existingSession = await firestoreService.validateTokenSession(tokenHash);

    if (!existingSession) {
      throw new Error('Invalid or expired token');
    }

    const ipAddress = await fetchUserIP();
    const userAgent = navigator.userAgent;

    await firestoreService.logActivity(existingSession.id, 'login', {
      ipAddress,
      userAgent,
    });

    setTokenUser({
      sessionId: existingSession.id,
      token: trimmedToken,
      expiresAt: existingSession.expiresAt.toMillis(),
    });
  };

  const logout = async () => {
    if (tokenUser) {
      const ipAddress = await fetchUserIP();
      const userAgent = navigator.userAgent;
      await firestoreService.logActivity(tokenUser.sessionId, 'logout', {
        ipAddress,
        userAgent,
      });
      setTokenUser(null);
    } else {
      await signOut(auth);
      setCurrentUser(null);
    }
  };

  const getRemainingTime = () => {
    if (!tokenUser) return null;
    return TokenService.getRemainingTime(tokenUser.expiresAt);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Check token expiration periodically
  useEffect(() => {
    if (!tokenUser) return;

    const checkExpiration = setInterval(() => {
      const remaining = TokenService.getRemainingTime(tokenUser.expiresAt);
      if (remaining.isExpired) {
        firestoreService.logActivity(tokenUser.sessionId, 'timeout').catch(console.error);
        setTokenUser(null);
      }
    }, 60000); // Check every minute

    return () => clearInterval(checkExpiration);
  }, [tokenUser]);

  const value = {
    currentUser,
    tokenUser,
    isTokenUser: !!tokenUser,
    signup,
    login,
    loginWithToken,
    logout,
    getRemainingTime,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

async function fetchUserIP(): Promise<string> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip || 'unknown';
  } catch {
    return 'unknown';
  }
}
